import { FilterPresetSelector } from "./FilterPresetSelector.js";
import {
  ChevronDown,
  Image as ImageIcon,
  Plus,
  Video,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Checkbox,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@nexus/ui";
import { OVERVIEW_ADVANCED_FILTER_FIELDS, AUDIENCE_SEGMENT_VALUES, IMPRESSION_DEVICE_VALUES, type DashboardModeType, type FilterPresetDto } from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { audienceSegmentLabel } from "@/lib/audience-segment-labels";
import { impressionDeviceLabel, selectionIncludesGoogle } from "@/lib/impression-device-labels";
import { resolveDashboardRequestWindow } from "@/features/dashboard/lib/resolve-dashboard-request-window";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";
import {
  myBrandFilterOptionsQueryOptions,
  type CompetitorFiltersInput,
} from "@/lib/queries/dashboard.queries";
import { derivePlatforms } from "@/features/dashboard/lib/my-brand-filter-options";
import {
  overviewMyBrandScenarioOptionsQueryOptions,
  overviewMyBrandSubFeatureOptionsQueryOptions,
  overviewScenarioOptionsQueryOptions,
  overviewSubFeatureOptionsQueryOptions,
} from "@/lib/queries/overview.queries";
import { AdvancedFiltersEditor } from "@/components/advanced-filters/AdvancedFiltersEditor";
import {
  ALL_AD_STATUSES_LABEL,
  COMPETITOR_EFFECTIVE_STATUS_OPTIONS,
  MY_BRAND_EFFECTIVE_STATUS_OPTIONS,
  MY_BRAND_INACTIVE_STATUSES,
} from "@/lib/ad-status";
import { useOverviewFiltersStore } from "../stores/overviewFilters.store.js";
import { useOverviewGroupByOptions } from "../hooks/useOverviewGroupByOptions.js";
import { subFeatureOptionsForScenarios } from "../lib/subFeatureOptions.js";
import {
  FILTER_TRIGGER_BASE,
  FILTER_TRIGGER_LABEL,
  FILTER_TRIGGER_VALUE,
} from "@/features/dashboard/lib/filter-trigger-styles";
import { OverviewSaveFilterPreset } from "@/features/dashboard/components/filter-presets/OverviewSaveFilterPreset";

// ─── Static filter options ────────────────────────────────────────────────────

const GROUP_BY_OPTIONS = [
  "Source",
  "Platform",
  "Position",
  "Device",
  "Age",
  "Gender",
  "Aspect_ratio",
  "Date",
];

const SECTION_OPTIONS = ["Hook", "Post-Hook", "None"];

const FORMAT_OPTIONS = ["Video", "Image", "Carousel"];

const FORMAT_ICONS: Record<string, LucideIcon> = {
  Video,
  Image: ImageIcon,
};

// TODO: replace with live performance-metric values from BQ (frontend-only sort dimension)
const PERFORMANCE_METRIC_OPTIONS = ["Days/Assets", "Days", "Assets"];

/** Retool's "No Active Ads" quick-select — see MY_BRAND_INACTIVE_STATUSES for why it is derived. */
const NO_ACTIVE_ADS_SHORTCUT = {
  label: "No Active Ads",
  values: [...MY_BRAND_INACTIVE_STATUSES],
};

// ─── DimensionPill (single + multi select) ────────────────────────────────────

interface DimensionPillProps {
  label: string;
  options: string[];
  selected: string | string[];
  multiSelect?: boolean;
  onSelect: (value: string | string[]) => void;
  formatLabel?: (v: string) => string;
  /** When provided, renders the circular clear-all button (Competitors design). */
  onClear?: () => void;
  /** Optional leading icon per option, keyed by option value (e.g. Format). */
  optionIcons?: Record<string, LucideIcon>;
  /** Trigger text when nothing is selected. Defaults to "All". */
  emptyLabel?: string;
  /** Quick-selects that replace the whole selection with a fixed value set. */
  shortcuts?: { label: string; values: string[] }[];
}

/** Set-equality for a shortcut's checked state — order and duplicates are irrelevant. */
function isSameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((v) => setB.has(v));
}

function DimensionPill({
  label,
  options,
  selected,
  multiSelect = false,
  onSelect,
  formatLabel,
  optionIcons,
  emptyLabel = "All",
  shortcuts,
}: DimensionPillProps) {
  const selectedArr = Array.isArray(selected) ? selected : selected ? [selected] : [];
  const display = formatLabel ?? ((v: string) => v);
  const allSelected = multiSelect && options.length > 0 && isSameSet(selectedArr, options);
  const showAll =
    multiSelect && (selectedArr.length === 0 || allSelected);
  const singleValue = !multiSelect && typeof selected === "string" && selected.length > 0;

  function handleToggle(opt: string) {
    if (!multiSelect) {
      onSelect(selected === opt ? "" : opt);
      return;
    }
    const arr = Array.isArray(selected) ? selected : [];
    onSelect(
      arr.includes(opt) ? arr.filter((v) => v !== opt) : [...arr, opt],
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label.toLowerCase()}
          className={cn(
            FILTER_TRIGGER_BASE,
            "inline-flex w-auto max-w-none items-center gap-1.5",
          )}
        >
          <span className={FILTER_TRIGGER_LABEL}>{label}:</span>
          {multiSelect ? (
            showAll ? (
              <span className={FILTER_TRIGGER_VALUE}>All</span>
            ) : (
              <span className={FILTER_TRIGGER_VALUE}>+{selectedArr.length}</span>
            )
          ) : singleValue ? (
            <span className={FILTER_TRIGGER_VALUE}>
              {display(selected as string)}
            </span>
          ) : (
            <span className="shrink-0 text-muted-foreground">{emptyLabel}</span>
          )}
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-1">
        <div className="max-h-56 overflow-y-auto">
          {multiSelect && options.length > 0 && (
            <label
              className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-accent"
            >
              <Checkbox
                checked={allSelected}
                onCheckedChange={() => onSelect(allSelected ? [] : [...options])}
                aria-label="All"
                className="h-3.5 w-3.5"
              />
              <span>All</span>
            </label>
          )}
          {shortcuts?.map((shortcut) => {
            const isActive = isSameSet(selectedArr, shortcut.values);
            return (
              <label
                key={shortcut.label}
                className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-accent"
              >
                <Checkbox
                  checked={isActive}
                  onCheckedChange={() => onSelect(isActive ? [] : [...shortcut.values])}
                  aria-label={shortcut.label}
                  className="h-3.5 w-3.5"
                />
                <span>{shortcut.label}</span>
              </label>
            );
          })}
          {multiSelect && options.length > 0 && (
            <div className="my-0.5 border-t border-border/50" />
          )}
          {options.map((opt) => {
            const isSelected = selectedArr.includes(opt);
            const Icon = optionIcons?.[opt];
            return (
              <label
                key={opt}
                className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(opt)}
                  aria-label={display(opt)}
                  className="h-3.5 w-3.5"
                />
                {Icon && <Icon className="size-3.5 shrink-0" aria-hidden />}
                <span>{display(opt)}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── StatusPill ───────────────────────────────────────────────────────────────

/**
 * Each mode owns its own status field and vocabulary: My Brand reads Meta's enum from
 * `core.new_feature_table_merged`, Competitors reads lowercase active/inactive from
 * `trump.competitor_raw_table`. See `@/lib/ad-status`.
 */
function StatusPill({ mode }: { mode: DashboardModeType }) {
  const effectiveStatus = useOverviewFiltersStore((s) => s.effectiveStatus);
  const myBrandEffectiveStatus = useOverviewFiltersStore((s) => s.myBrandEffectiveStatus);
  const setFilter = useOverviewFiltersStore((s) => s.setFilter);

  const isMyBrand = mode === "my-brand";
  const optionSet = isMyBrand
    ? MY_BRAND_EFFECTIVE_STATUS_OPTIONS
    : COMPETITOR_EFFECTIVE_STATUS_OPTIONS;
  const field = isMyBrand ? "myBrandEffectiveStatus" : "effectiveStatus";
  const selected = isMyBrand ? myBrandEffectiveStatus : effectiveStatus;

  return (
    <DimensionPill
      label="Status"
      // Competitors keeps its existing "All" placeholder; only My Brand adopts Retool's wording.
      emptyLabel={isMyBrand ? ALL_AD_STATUSES_LABEL : undefined}
      options={optionSet.map((o) => o.value)}
      selected={selected}
      multiSelect
      onSelect={(v) => setFilter(field, v as string[])}
      formatLabel={(v) => optionSet.find((o) => o.value === v)?.label ?? v}
      shortcuts={isMyBrand ? [NO_ACTIVE_ADS_SHORTCUT] : undefined}
    />
  );
}

// ─── Add-filter dropdown (dashed) ──────────────────────────────────────────────

function AddFilterDropdown({
  withLabel = false,
  mode,
  onRuleAdded,
}: {
  withLabel?: boolean;
  mode: DashboardModeType;
  onRuleAdded: () => void;
}) {
  const advancedFilters = useOverviewFiltersStore((s) => s.advancedFilters);
  const addFilterGroup = useOverviewFiltersStore((s) => s.addFilterGroup);
  const addFilterRule = useOverviewFiltersStore((s) => s.addFilterRule);
  const firstField = OVERVIEW_ADVANCED_FILTER_FIELDS[mode][0];
  const defaultField = firstField?.key ?? "Days";
  // Seed a valid default operator for the field type (numeric fields reject "is").
  const defaultOperator = firstField?.type === "numeric" ? "gt" : "is";

  // "Add filter rule" appends to the last group, or starts one if none exist.
  const handleAddRule = () => {
    if (advancedFilters.length === 0) addFilterGroup(defaultField, defaultOperator);
    else addFilterRule(advancedFilters.length - 1, defaultField, defaultOperator);
    onRuleAdded();
  };

  const handleAddGroup = () => {
    addFilterGroup(defaultField);
    onRuleAdded();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="add filter"
          className={cn(
            FILTER_TRIGGER_BASE,
            "inline-flex items-center gap-1.5 border-dashed text-muted-foreground hover:text-foreground",
            withLabel ? "px-3" : "px-2",
          )}
        >
          <Plus className="size-3.5 shrink-0" aria-hidden />
          {withLabel && <span>Filter</span>}
        </button>
      </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          <DropdownMenuItem onSelect={handleAddRule}>Add filter rule</DropdownMenuItem>
        <DropdownMenuItem onSelect={handleAddGroup}>Add filter group</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Advanced-filter editor row (draft → Apply/Discard) ────────────────────────

function AdvancedFilterRow({
  mode,
  isExpanded,
  onExpandedChange,
}: {
  mode: DashboardModeType;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
}) {
  const {
    advancedFilters,
    setGroupConnector,
    addFilterRule,
    removeFilterGroup,
    updateFilterRule,
    removeFilterRule,
  } = useOverviewFiltersStore();

  const fieldOptions = OVERVIEW_ADVANCED_FILTER_FIELDS[mode];
  const firstField = fieldOptions[0];
  const defaultField = firstField?.key ?? "Days";
  const defaultOperator = firstField?.type === "numeric" ? "gt" : "is";

  if (advancedFilters.length === 0) return null;

  // Draft edits only — commit happens via the global "Apply changes" button (ExploreFilterHeader).
  return (
    <div className="border-t border-border/50 px-5 py-3" data-testid="overview-advanced-filters">
      <AdvancedFiltersEditor
        groups={advancedFilters}
        fieldOptions={fieldOptions}
        isExpanded={isExpanded}
        onExpandedChange={onExpandedChange}
        onSetConnector={setGroupConnector}
        onAddRule={(gIdx) => addFilterRule(gIdx, defaultField, defaultOperator)}
        onRemoveGroup={removeFilterGroup}
        onUpdateRule={updateFilterRule}
        onRemoveRule={removeFilterRule}
      />
    </div>
  );
}

// ─── OverviewFilterBar ────────────────────────────────────────────────────────

export function OverviewFilterBar({
  trailingActions,
  onPresetApplied,
  filtersHydrated = true,
  applySignal,
  onPendingPresetChange,
  canSavePreset = true,
}: {
  trailingActions?: ReactNode;
  onPresetApplied?: (preset: FilterPresetDto) => void;
  filtersHydrated?: boolean;
  applySignal?: number;
  onPendingPresetChange?: (hasPending: boolean) => void;
  canSavePreset?: boolean;
}) {
  const mode = useDashboardFiltersStore((s) => s.mode);
  const [selectPresetId, setSelectPresetId] = useState<string | null>(null);
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  const handlePresetApplied = (preset: FilterPresetDto) => {
    setIsAdvancedExpanded(false);
    onPresetApplied?.(preset);
  };

  return mode === "competitors"
    ? (
        <CompetitorsFilterBar
          trailingActions={trailingActions}
          onPresetApplied={handlePresetApplied}
          filtersHydrated={filtersHydrated}
          applySignal={applySignal}
          onPendingPresetChange={onPendingPresetChange}
          canSavePreset={canSavePreset}
          selectPresetId={selectPresetId}
          onPresetCreated={setSelectPresetId}
          isAdvancedExpanded={isAdvancedExpanded}
          onAdvancedExpandedChange={setIsAdvancedExpanded}
        />
      )
    : (
        <MyBrandFilterBar
          trailingActions={trailingActions}
          onPresetApplied={handlePresetApplied}
          filtersHydrated={filtersHydrated}
          applySignal={applySignal}
          onPendingPresetChange={onPendingPresetChange}
          canSavePreset={canSavePreset}
          selectPresetId={selectPresetId}
          onPresetCreated={setSelectPresetId}
          isAdvancedExpanded={isAdvancedExpanded}
          onAdvancedExpandedChange={setIsAdvancedExpanded}
        />
      );
}

// ─── My Brand layout ──────────────────────────────────────────────────────────

function MyBrandFilterBar({
  trailingActions,
  onPresetApplied,
  filtersHydrated,
  applySignal,
  onPendingPresetChange,
  canSavePreset,
  selectPresetId,
  onPresetCreated,
  isAdvancedExpanded,
  onAdvancedExpandedChange,
}: {
  trailingActions?: ReactNode;
  onPresetApplied?: (preset: FilterPresetDto) => void;
  filtersHydrated?: boolean;
  applySignal?: number;
  onPendingPresetChange?: (hasPending: boolean) => void;
  canSavePreset: boolean;
  selectPresetId: string | null;
  onPresetCreated: (presetId: string) => void;
  isAdvancedExpanded: boolean;
  onAdvancedExpandedChange: (isExpanded: boolean) => void;
}) {
  const { t } = useTranslation();
  const { scenario, subFeature, featureTimeFrame, objectType, audienceSegment, device, groupBy, groupByValues, setFilter } =
    useOverviewFiltersStore();
  const appliedDevice = useOverviewFiltersStore((s) => s.applied.device);
  const clearDevice = useOverviewFiltersStore((s) => s.clearDevice);

  // Scenario options come from the features_mapping table's core rows (platform='core'),
  // scoped to the tenant's customerPartition (→ defaultBrand) BQ client name(s) — i.e. the
  // scenarios that actually exist in this tenant's core.full_reco_table data.
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const { data: scenarioOptions } = useQuery(
    overviewMyBrandScenarioOptionsQueryOptions(activeTenantId),
  );
  // Sub-categories options are scenario-specific: union the sub-features of the
  // currently selected scenario(s) from the core features_mapping data. Derived from
  // the draft `scenario`, so the pill reflects edits before they're applied.
  const { data: subFeatureMap } = useQuery(
    overviewMyBrandSubFeatureOptionsQueryOptions(activeTenantId),
  );
  const subFeatureOptions = subFeatureOptionsForScenarios(subFeatureMap ?? {}, scenario);

  // Group By value sub-filter: distinct values of the selected dimension, fetched live
  // from BQ (My-Brand mode). Scoped to the applied My-Brand date range — the same window
  // the summary chart uses. Disabled/absent for the time-based "Date" dimension.
  const appliedMyBrandFilters = useDashboardFiltersStore((s) => s.appliedMyBrandFilters);
  const draftPlatforms = useDashboardFiltersStore((s) => s.myBrandFilters.platforms);
  const dateRange = appliedMyBrandFilters.dateRange;
  const groupByWindow = resolveDashboardRequestWindow(appliedMyBrandFilters);
  const { options: groupByOptions } = useOverviewGroupByOptions(
    activeTenantId,
    groupBy,
    dateRange,
    groupByWindow,
  );
  const showGroupByValues = !!groupBy && groupBy !== "Date";
  const { data: filterOptions, isFetched: platformsFetched } = useQuery(
    myBrandFilterOptionsQueryOptions(activeTenantId, true, "/overview"),
  );
  const availablePlatformIds = useMemo(
    () => derivePlatforms(filterOptions?.tuples ?? []).map((option) => option.id),
    [filterOptions],
  );
  // "All" has no platform list of its own. Until the tenant's platforms arrive, keep a
  // saved device selection instead of treating the empty default as a Meta-only tenant.
  // Visibility follows the draft platforms so the pill appears as the dropdown changes,
  // before Apply. Draft-only clear leaves applied alone until Apply; applied clear drops
  // stale committed device when applied platforms exclude Google.
  const appliedPlatforms = appliedMyBrandFilters.platforms;
  const platformsKnown = draftPlatforms !== null || platformsFetched;
  const appliedPlatformsKnown = appliedPlatforms !== null || platformsFetched;
  const showDevice =
    platformsKnown &&
    selectionIncludesGoogle(draftPlatforms, availablePlatformIds);
  const appliedIncludesGoogle = selectionIncludesGoogle(appliedPlatforms, availablePlatformIds);
  useEffect(() => {
    if (platformsKnown && !showDevice && device.length > 0) setFilter("device", []);
  }, [platformsKnown, showDevice, device.length, setFilter]);
  useEffect(() => {
    if (appliedPlatformsKnown && !appliedIncludesGoogle && appliedDevice.length > 0) clearDevice();
  }, [appliedPlatformsKnown, appliedIncludesGoogle, appliedDevice.length, clearDevice]);

  return (
    <div data-overview-filter-bar>
      <div className="flex flex-wrap items-center gap-2 border-t border-border/50 px-5 py-2">
        <DimensionPill
          label="Scenario"
          options={scenarioOptions ?? []}
          selected={scenario}
          multiSelect
          onSelect={(v) => setFilter("scenario", v as string[])}
        />
        <DimensionPill
          label="Sub-categories"
          options={subFeatureOptions}
          selected={subFeature}
          multiSelect
          onSelect={(v) => setFilter("subFeature", v as string[])}
        />
        <DimensionPill
          label="Section"
          options={SECTION_OPTIONS}
          selected={featureTimeFrame}
          multiSelect
          onSelect={(v) => setFilter("featureTimeFrame", v as string[])}
        />
        <DimensionPill
          label="Format"
          options={FORMAT_OPTIONS}
          selected={objectType}
          multiSelect
          onSelect={(v) => setFilter("objectType", v as string[])}
          optionIcons={FORMAT_ICONS}
        />
        <DimensionPill
          label={t("overview.filters.audienceSegment", "Audience segment")}
          options={[...AUDIENCE_SEGMENT_VALUES]}
          selected={audienceSegment}
          multiSelect
          formatLabel={(v) => audienceSegmentLabel(t, "overview", v)}
          onSelect={(v) => setFilter("audienceSegment", v as string[])}
        />
        {showDevice ? (
          <DimensionPill
            label={t("overview.filters.device", "Device")}
            options={[...IMPRESSION_DEVICE_VALUES]}
            selected={device}
            multiSelect
            formatLabel={(v) => impressionDeviceLabel(t, "overview", v)}
            onSelect={(v) => setFilter("device", v as string[])}
          />
        ) : null}
        <DimensionPill
          label="Group By"
          options={GROUP_BY_OPTIONS}
          selected={groupBy}
          onSelect={(v) => setFilter("groupBy", v as string)}
        />
        {showGroupByValues && (
          <DimensionPill
            label={groupBy.replace(/_/g, " ")}
            options={groupByOptions}
            selected={groupByValues}
            multiSelect
            onSelect={(v) => setFilter("groupByValues", v as string[])}
          />
        )}
        <StatusPill mode="my-brand" />

        <AddFilterDropdown mode="my-brand" onRuleAdded={() => onAdvancedExpandedChange(true)} />

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <FilterPresetSelector
            mode="my-brand"
            filtersHydrated={filtersHydrated}
            onPresetApplied={onPresetApplied}
            applySignal={applySignal}
            onPendingPresetChange={onPendingPresetChange}
            selectPresetId={selectPresetId}
          />
          <OverviewSaveFilterPreset
            mode="my-brand"
            canSavePreset={canSavePreset}
            onPresetCreated={onPresetCreated}
          />
          {trailingActions}
        </div>
      </div>

      {/* Advanced filters (draft → Apply) */}
      <AdvancedFilterRow
        mode="my-brand"
        isExpanded={isAdvancedExpanded}
        onExpandedChange={onAdvancedExpandedChange}
      />
    </div>
  );
}

// ─── Competitors layout ───────────────────────────────────────────────────────

function CompetitorsFilterBar({
  trailingActions,
  onPresetApplied,
  filtersHydrated,
  applySignal,
  onPendingPresetChange,
  canSavePreset,
  selectPresetId,
  onPresetCreated,
  isAdvancedExpanded,
  onAdvancedExpandedChange,
}: {
  trailingActions?: ReactNode;
  onPresetApplied?: (preset: FilterPresetDto) => void;
  filtersHydrated?: boolean;
  applySignal?: number;
  onPendingPresetChange?: (hasPending: boolean) => void;
  canSavePreset: boolean;
  selectPresetId: string | null;
  onPresetCreated: (presetId: string) => void;
  isAdvancedExpanded: boolean;
  onAdvancedExpandedChange: (isExpanded: boolean) => void;
}) {
  const {
    scenario,
    subFeature,
    performanceMetric,
    featureTimeFrame,
    objectType,
    setFilter,
  } = useOverviewFiltersStore();

  // Scenario options come from the features_mapping table, scoped to the
  // selected competitor/industry entity context (same applied filters that
  // drive the Competitors summary, so keys stay consistent).
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const appliedCompetitorFilters = useDashboardFiltersStore((s) => s.appliedCompetitorFilters);
  const competitorFilters = useMemo<CompetitorFiltersInput>(
    () => ({
      myIndustry: appliedCompetitorFilters.myIndustry,
      myBrand: appliedCompetitorFilters.myBrand,
      industryProducts: appliedCompetitorFilters.industryProducts,
      competitorsIndustry: appliedCompetitorFilters.competitorsIndustry,
      mainCompetitor: appliedCompetitorFilters.mainCompetitor,
      benchmark: appliedCompetitorFilters.benchmark,
      platforms: appliedCompetitorFilters.platforms,
      dateRange: appliedCompetitorFilters.dateRange as CompetitorFiltersInput["dateRange"],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(appliedCompetitorFilters)],
  );
  const { data: scenarioOptions } = useQuery(
    overviewScenarioOptionsQueryOptions(activeTenantId, competitorFilters),
  );
  const { data: subFeatureMap } = useQuery(
    overviewSubFeatureOptionsQueryOptions(activeTenantId, competitorFilters),
  );
  const subFeatureOptions = subFeatureOptionsForScenarios(subFeatureMap ?? {}, scenario);

  return (
    <div data-overview-filter-bar>
      <div className="flex flex-wrap items-center gap-2 border-t border-border/50 px-5 py-2">
        <DimensionPill
          label="Scenario"
          options={scenarioOptions ?? []}
          selected={scenario}
          multiSelect
          onSelect={(v) => setFilter("scenario", v as string[])}
        />
        <DimensionPill
          label="Sub-categories"
          options={subFeatureOptions}
          selected={subFeature}
          multiSelect
          onSelect={(v) => setFilter("subFeature", v as string[])}
        />
        <DimensionPill
          label="Performance Metric"
          options={PERFORMANCE_METRIC_OPTIONS}
          selected={performanceMetric}
          onSelect={(v) => setFilter("performanceMetric", v as string)}
        />
        <DimensionPill
          label="Section"
          options={SECTION_OPTIONS}
          selected={featureTimeFrame}
          multiSelect
          onSelect={(v) => setFilter("featureTimeFrame", v as string[])}
        />
        <DimensionPill
          label="Format"
          options={FORMAT_OPTIONS}
          selected={objectType}
          multiSelect
          onSelect={(v) => setFilter("objectType", v as string[])}
          optionIcons={FORMAT_ICONS}
        />
        <StatusPill mode="competitors" />

        <AddFilterDropdown
          withLabel
          mode="competitors"
          onRuleAdded={() => onAdvancedExpandedChange(true)}
        />

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <FilterPresetSelector
            mode="competitors"
            filtersHydrated={filtersHydrated}
            onPresetApplied={onPresetApplied}
            applySignal={applySignal}
            onPendingPresetChange={onPendingPresetChange}
            selectPresetId={selectPresetId}
          />
          <OverviewSaveFilterPreset
            mode="competitors"
            canSavePreset={canSavePreset}
            onPresetCreated={onPresetCreated}
          />
          {trailingActions}
        </div>
      </div>

      {/* Advanced filters (draft → Apply) */}
      <AdvancedFilterRow
        mode="competitors"
        isExpanded={isAdvancedExpanded}
        onExpandedChange={onAdvancedExpandedChange}
      />
    </div>
  );
}
